import React, { useState } from 'react';
import { Modal } from 'antd';
import { Button as BaseButton } from '@packages/utils';
import styled from 'styled-components';
import { DatePicker } from 'components/Base/DatePicker';
import dayjs from 'dayjs';

const StyledDateSelectModal = styled(Modal)`
  .ant-modal-content {
    padding: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08),
      0px 3px 6px -4px rgba(0, 0, 0, 0.12);
    border: none;
  }

  .ant-modal-header {
    background-color: white;
    padding: 24px;
    margin: 0;
    border-bottom: none;
  }

  .ant-modal-title {
    color: #1b1a1c;
    font-family: Montserrat;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  .ant-modal-body {
    padding: 0 24px 24px;
  }

  .ant-modal-footer {
    display: flex;
    justify-content: flex-end;
    padding: 16px 24px;
    margin-top: 24px;
    border-top: none;
  }

  .ant-modal-close {
    top: 20px;
    right: 20px;
  }

  .ant-modal-close-x {
    font-size: 42px;
    font-weight: 300;
    width: 24px;
    height: 24px;
    line-height: 24px;
  }
`;

const ShipToText = styled.div`
  margin-bottom: 24px;
  color: #1b1a1c;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 23px;

  .ship-to-label {
    font-weight: 700;
  }
`;

const DatePickerWrapper = styled.div`
  margin-bottom: 16px;
  width: 50%;

  .date-label {
    font-size: 16px;
    margin-bottom: 8px;
    display: block;

    .required {
      color: red;
      margin-left: 4px;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled(BaseButton)`
  border-radius: 8px;
  height: 40px;
  min-width: 100px;
`;

const SaveButton = styled(BaseButton)`
  border-radius: 8px;
  height: 40px;
  min-width: 100px;
  background-color: #0077a0;
  border-color: #0077a0;

  &:hover,
  &:focus {
    background-color: #006699;
    border-color: #006699;
  }
`;

export interface DateSelectModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (date: string) => void;
  address: string;
  isLoading?: boolean;
}

const DateSelectModal: React.FC<DateSelectModalProps> = ({
  visible,
  onClose,
  onSave,
  address,
  isLoading = false,
}) => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

  const handleSave = () => {
    if (selectedDate) {
      onSave(selectedDate.format('MMMM DD, YYYY'));
    }
  };

  return (
    <StyledDateSelectModal
      title="Select a Date"
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      closeIcon={<span>×</span>}
    >
      <ShipToText>
        <span className="ship-to-label">Ship to:</span> {address}
      </ShipToText>

      <DatePickerWrapper>
        <label className="date-label">
          Date<span className="required">*</span>
        </label>
        <DatePicker
          value={selectedDate}
          onChange={date => setSelectedDate(date)}
          style={{ width: '100%' }}
          format="MMMM DD, YYYY"
          disabled={isLoading}
        />
      </DatePickerWrapper>

      <ModalFooter>
        <CancelButton onClick={onClose} color="primary" disabled={isLoading}>
          Cancel
        </CancelButton>
        <SaveButton
          type="primary"
          onClick={handleSave}
          color="primary"
          loading={isLoading}
          disabled={isLoading || !selectedDate}
        >
          Save
        </SaveButton>
      </ModalFooter>
    </StyledDateSelectModal>
  );
};

export default DateSelectModal;
